/**
 * FeedbackWidget Component
 * Widget for collecting user feedback on AI summaries
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Space, Typography, message } from 'antd';
import {
    LikeOutlined,
    DislikeOutlined,
    LikeFilled,
    DislikeFilled,
    CheckCircleOutlined
} from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const FeedbackContainer = styled.div`
    padding: 16px;
    background: #fafafa;
    border-radius: 8px;
    text-align: center;
`;

const FeedbackQuestion = styled.div`
    margin-bottom: 12px;
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
`;

const FeedbackButton = styled(Button)`
    min-width: 100px;

    &.selected-helpful {
        background: #f6ffed;
        border-color: #52c41a;
        color: #52c41a;
    }

    &.selected-not-helpful {
        background: #fff1f0;
        border-color: #f5222d;
        color: #f5222d;
    }
`;

const CommentSection = styled.div`
    margin-top: 12px;
`;

const SuccessMessage = styled.div`
    color: #52c41a;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

/**
 * FeedbackWidget Component
 * @param {Object} props - Component props
 * @param {Function} props.onFeedback - Callback when feedback is submitted
 * @param {boolean} props.disabled - Whether feedback is disabled
 * @param {string} props.error - Error message to display
 */
function FeedbackWidget({ onFeedback, disabled = false, error }) {
    const [helpful, setHelpful] = useState(null);
    const [comment, setComment] = useState('');
    const [showComment, setShowComment] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleHelpfulClick = (isHelpful) => {
        if (disabled || submitted) return;

        setHelpful(isHelpful);
        setShowComment(true);
    };

    const handleSubmit = async () => {
        if (helpful === null || disabled || submitted) return;

        setSubmitting(true);
        try {
            await onFeedback(helpful, null, comment || null);
            setSubmitted(true);
            message.success('Thank you for your feedback!');
        } catch (err) {
            message.error(err.message || 'Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSkip = async () => {
        if (helpful === null || disabled || submitted) return;

        setSubmitting(true);
        try {
            await onFeedback(helpful, null, null);
            setSubmitted(true);
            message.success('Thank you for your feedback!');
        } catch (err) {
            message.error(err.message || 'Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <FeedbackContainer>
                <SuccessMessage>
                    <CheckCircleOutlined />
                    <Text>Thank you for your feedback!</Text>
                </SuccessMessage>
            </FeedbackContainer>
        );
    }

    if (disabled) {
        return null;
    }

    return (
        <FeedbackContainer>
            <FeedbackQuestion>
                <Text>Was this summary helpful?</Text>
            </FeedbackQuestion>

            <ButtonGroup>
                <FeedbackButton
                    icon={helpful === true ? <LikeFilled /> : <LikeOutlined />}
                    onClick={() => handleHelpfulClick(true)}
                    className={helpful === true ? 'selected-helpful' : ''}
                >
                    Yes
                </FeedbackButton>
                <FeedbackButton
                    icon={helpful === false ? <DislikeFilled /> : <DislikeOutlined />}
                    onClick={() => handleHelpfulClick(false)}
                    className={helpful === false ? 'selected-not-helpful' : ''}
                >
                    No
                </FeedbackButton>
            </ButtonGroup>

            {showComment && (
                <CommentSection>
                    <TextArea
                        placeholder="Any additional comments? (optional)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={2}
                        maxLength={500}
                        style={{ marginBottom: 12 }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={submitting}
                            style={{ backgroundColor: '#F16622', borderColor: '#F16622' }}
                        >
                            Submit Feedback
                        </Button>
                        <Button onClick={handleSkip} loading={submitting}>
                            Skip Comment
                        </Button>
                    </Space>
                </CommentSection>
            )}

            {error && (
                <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                    {error}
                </Text>
            )}
        </FeedbackContainer>
    );
}

export default FeedbackWidget;
